// Desktop surface layout (CR #133): wallpaper choice, widget placement and the
// icon/folder tree. Persisted per user so two people sharing a browser profile
// don't inherit each other's desktop; the whole thing is client-side because a
// trial feature shouldn't grow a schema until it's proven.
import type { AppMenuItem } from '@/components/desktop/appMenu';

const LS_PREFIX = 'desktop_layout_v1';
const EVT = 'desktop-layout-changed';

// ── Wallpapers ───────────────────────────────────────────────────────────────
// `css` is a CSS `background` shorthand value. `light` flips icon-label colours
// so text stays legible on the pale option.
export interface Wallpaper { id: string; label: string; css: string; light?: boolean }

export const WALLPAPERS: Wallpaper[] = [
  {
    id: 'ochre',
    label: 'Ochre Dusk',
    css:
      'radial-gradient(circle at 18% 18%, rgba(199,125,63,0.55), transparent 55%),' +
      'radial-gradient(circle at 82% 74%, rgba(139,90,43,0.50), transparent 52%),' +
      'linear-gradient(160deg, #2C1810 0%, #3d2418 45%, #1e1e2e 100%)',
  },
  {
    id: 'slate',
    label: 'Workshop Slate',
    css:
      'radial-gradient(circle at 75% 20%, rgba(90,100,140,0.35), transparent 55%),' +
      'linear-gradient(160deg, #1e1e2e 0%, #2b2b3f 55%, #141420 100%)',
  },
  {
    id: 'cream',
    label: 'Cedar Cream',
    light: true,
    css:
      'radial-gradient(circle at 15% 12%, rgba(199,125,63,0.28), transparent 52%),' +
      'radial-gradient(circle at 85% 85%, rgba(139,90,43,0.22), transparent 50%),' +
      'linear-gradient(150deg, #FAF6F0 0%, #F1E5D7 60%, #E4D3BF 100%)',
  },
];

// ── Widgets ──────────────────────────────────────────────────────────────────

export type WidgetKind =
  | 'clock' | 'stats' | 'weather' | 'calendar'
  | 'alerts' | 'attention' | 'upcoming' | 'dailylog'
  | 'tasklists' | 'revenue' | 'pipeline' | 'activity' | 'quickactions';

export interface WidgetSpec {
  kind: WidgetKind;
  label: string;
  icon: string;
  w: number;
  h: number;
  /** Component already draws its own card — don't double up the chrome. */
  bare?: boolean;
}

export const WIDGET_CATALOG: WidgetSpec[] = [
  { kind: 'clock', label: 'Clock & Date', icon: 'schedule', w: 260, h: 150 },
  { kind: 'stats', label: 'Business Snapshot', icon: 'query_stats', w: 340, h: 260 },
  { kind: 'weather', label: 'Site Weather', icon: 'partly_cloudy_day', w: 420, h: 190, bare: true },
  { kind: 'calendar', label: 'Mini Calendar', icon: 'calendar_month', w: 320, h: 330 },
  { kind: 'alerts', label: 'Alerts & Approvals', icon: 'notifications_active', w: 360, h: 300 },
  { kind: 'attention', label: 'Needs Attention', icon: 'error', w: 360, h: 300 },
  { kind: 'upcoming', label: 'Upcoming Jobs', icon: 'construction', w: 420, h: 320 },
  { kind: 'dailylog', label: 'Quick Daily Log', icon: 'edit_note', w: 360, h: 340 },
  // CR #184 — the rest of the dashboard's applets, so the desktop can carry
  // the whole dashboard as widgets.
  { kind: 'tasklists', label: 'Job Task Lists', icon: 'checklist', w: 440, h: 380, bare: true },
  { kind: 'revenue', label: 'Revenue Trend', icon: 'bar_chart', w: 440, h: 280 },
  { kind: 'pipeline', label: 'Jobs Pipeline', icon: 'donut_small', w: 340, h: 280 },
  { kind: 'activity', label: 'Recent Activity', icon: 'history', w: 380, h: 320 },
  { kind: 'quickactions', label: 'Quick Actions', icon: 'bolt', w: 300, h: 150 },
];

export const widgetSpec = (kind: WidgetKind): WidgetSpec =>
  WIDGET_CATALOG.find(w => w.kind === kind) ?? WIDGET_CATALOG[0];

export interface DesktopWidget {
  id: string;
  kind: WidgetKind;
  x: number;
  y: number;
  w: number;
  h: number;
}

// ── Icons (shortcuts + folders) ──────────────────────────────────────────────

export interface DesktopIcon {
  id: string;
  kind: 'shortcut' | 'folder';
  label: string;
  icon: string;
  /** Shortcuts only — the in-app route the window opens. */
  route?: string;
  /** null = loose on the wallpaper; otherwise the containing folder's id. */
  parentId: string | null;
  x: number;
  y: number;
}

export interface DesktopLayout {
  wallpaper: string;
  /** Optional custom background image URL — wins over the built-in gradient. */
  wallpaperUrl: string;
  widgets: DesktopWidget[];
  icons: DesktopIcon[];
  /** CR #184 one-time upgrade marker: the full dashboard-applet widget set has
   *  been placed once; after that the user's own add/remove choices win. */
  dashUpgradeV184?: boolean;
}

export const uid = () => `d${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

// Icons flow down the left edge; widgets start to their right so a first-run
// desktop looks arranged rather than piled in one corner.
const ICON_X = 24;
const ICON_STEP = 108;

const seedIcons = (): DesktopIcon[] =>
  ([
    { label: 'Dashboard', icon: 'dashboard', route: '/dashboard' },
    { label: 'Jobs', icon: 'construction', route: '/jobs' },
    { label: 'Clients', icon: 'groups', route: '/clients' },
    { label: 'Calendar', icon: 'calendar_month', route: '/calendar' },
    { label: 'Timesheets', icon: 'schedule', route: '/timesheets' },
  ] as const).map((s, i) => ({
    id: uid(),
    kind: 'shortcut' as const,
    label: s.label,
    icon: s.icon,
    route: s.route,
    parentId: null,
    x: ICON_X,
    y: 24 + i * ICON_STEP,
  }));

/** CR #184 — the dashboard rebuilt as a tiled widget desktop. Three columns to
 *  the right of the icon rail, sized to fit a ~1440×900 screen with no
 *  overlap: the dashboard's primary applets (jobs, task lists, daily log,
 *  stats, quick actions, weather, schedule, alerts) are placed; the secondary
 *  ones (clock, attention, revenue, pipeline, activity) stay one click away in
 *  the Widgets dialog. All 13 widgets can't tile in one viewport — a curated
 *  set beats a pile of overlapping cards. Applied once (dashUpgradeV184);
 *  after that the user's arrangement wins. */
export function dashboardPresetWidgets(): DesktopWidget[] {
  const at = (kind: WidgetKind, x: number, y: number, w: number, h: number): DesktopWidget =>
    ({ id: uid(), kind, x, y, w, h });
  return [
    // Column A — the working list
    at('upcoming', 170, 72, 400, 250),
    at('tasklists', 170, 334, 400, 390),
    // Column B — numbers + quick input
    at('stats', 582, 72, 400, 230),
    at('dailylog', 582, 314, 400, 330),
    at('quickactions', 582, 656, 400, 120),
    // Column C — conditions + schedule + approvals
    at('weather', 994, 72, 420, 180),
    at('calendar', 994, 264, 420, 290),
    at('alerts', 994, 566, 420, 210),
  ];
}

export const defaultLayout = (): DesktopLayout => ({
  wallpaper: 'ochre',
  wallpaperUrl: '',
  widgets: dashboardPresetWidgets(),
  icons: seedIcons(),
  dashUpgradeV184: true,
});

const keyFor = (userId?: number) => `${LS_PREFIX}_${userId ?? 'anon'}`;

export function loadLayout(userId?: number): DesktopLayout {
  try {
    const raw = localStorage.getItem(keyFor(userId));
    if (!raw) return defaultLayout();
    const parsed = JSON.parse(raw) as Partial<DesktopLayout>;
    const widgets = Array.isArray(parsed.widgets) ? parsed.widgets.filter(w => w && w.id && w.kind) : [];
    return {
      wallpaper: typeof parsed.wallpaper === 'string' ? parsed.wallpaper : 'ochre',
      wallpaperUrl: typeof parsed.wallpaperUrl === 'string' ? parsed.wallpaperUrl : '',
      // #184: one-time — swap the old 4-widget trial seed for the tiled
      // dashboard preset (pre-#184 layouts were the untouched default seed)
      widgets: parsed.dashUpgradeV184 ? widgets : dashboardPresetWidgets(),
      icons: Array.isArray(parsed.icons) ? parsed.icons.filter(i => i && i.id && i.kind) : [],
      dashUpgradeV184: true,
    };
  } catch {
    return defaultLayout();
  }
}

export function saveLayout(userId: number | undefined, layout: DesktopLayout) {
  try { localStorage.setItem(keyFor(userId), JSON.stringify(layout)); } catch { /* quota */ }
  window.dispatchEvent(new Event(EVT));
}

export const shortcutFromMenuItem = (item: AppMenuItem, x: number, y: number): DesktopIcon => ({
  id: uid(),
  kind: 'shortcut',
  label: item.label,
  icon: item.icon,
  route: item.href,
  parentId: null,
  x,
  y,
});
